import { useAuth0 } from "@auth0/auth0-react";
import { PropsWithChildren, useEffect, useMemo } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";
import { useMutation } from "urql";

import { utmKeys } from "@/constants/utm";
import { LogWorkspaceActivity } from "@/hooks/queries.graphql";
import { getReferrerUrl } from "@/utils/referrer";

import {
  useNextParam,
  useQueryWithResponseHandler,
  useReducerWithURLParams,
} from "../../hooks";
import { NullDataError } from "../errors/NullDataError";
import { SyncUser } from "./queries.graphql";

type UtmParams = {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
};

export const ValidAccountGuard = (props: PropsWithChildren) => {
  const { children = <Outlet /> } = props;
  const { user } = useAuth0();
  const params = useParams<{ workspace?: string }>();
  const nextParam = useNextParam();

  const [, logWorkspaceActivity] = useMutation(LogWorkspaceActivity);

  const [utmParams] = useReducerWithURLParams<UtmParams, Record<string, never>>(
    (payload) => payload,
    {},
    {
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_content: null,
      utm_term: null,
    },
  );

  if (user == null) throw new NullDataError();

  const name = user.name || "";
  const firstName = user.given_name || "";
  const lastName = user.family_name || "";
  const profilePicture = user.picture || "";

  // Resolve UTM params from URL or localStorage. They can be lost when the user is signing up with email verification.
  const resolvedUtm = useMemo(() => {
    const hasUrlUtm = utmKeys.some(
      (key) => utmParams[key as keyof UtmParams] !== null,
    );
    if (hasUrlUtm) return utmParams;

    const stored = localStorage.getItem("layer_utm_params");
    if (!stored) return utmParams;

    try {
      const { params: lsParams, expires } = JSON.parse(stored);
      if (!lsParams) return utmParams;

      const isExpired = Date.now() > expires;
      return isExpired ? utmParams : lsParams;
    } catch (error) {
      console.error(error);
      return utmParams;
    }
  }, [utmParams]);

  const resolvedReferrerUrl = useMemo(() => {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem("layer_referrer_url");
    } catch (error) {
      console.error(error);
    }
    return stored || getReferrerUrl() || null;
  }, []);

  // NB: This is a query but may mutate backend state if the user doesn't exist.
  // We do this to make sure that the user is synced in our database and use a
  // query so that we can leverage suspense to block the rest of the app.
  const {
    data: { user: userResponse },
  } = useQueryWithResponseHandler(
    {
      query: SyncUser,
      variables: {
        input: {
          name,
          firstName,
          lastName,
          profilePicture,
          utmSource: resolvedUtm.utm_source,
          utmMedium: resolvedUtm.utm_medium,
          utmCampaign: resolvedUtm.utm_campaign,
          utmContent: resolvedUtm.utm_content,
          utmTerm: resolvedUtm.utm_term,
          referrerUrl: resolvedReferrerUrl,
        },
      },
      requestPolicy: "network-only", // todo: @tuizi this should be adjusted when the Auth process is reviewed. Prevent infinite login loop.
    },
    {
      redirectOnError: true,
    },
  );

  useEffect(() => {
    if (!userResponse) return;
    try {
      localStorage.removeItem("layer_utm_params");
      localStorage.removeItem("layer_referrer_url");
    } catch (error) {
      console.error(error);
    }
  }, [userResponse]);

  useEffect(() => {
    if (params.workspace) {
      logWorkspaceActivity({
        input: {
          workspaceName: params.workspace,
          activityType: "login",
        },
      });
    }
  }, [params.workspace, logWorkspaceActivity]);

  if (!userResponse) {
    return null;
  }

  if (!userResponse.acceptedTos) {
    return <Navigate to={`/tos?next=${nextParam}`} />;
  }

  return children;
};
